import React, { useState } from 'react';
import { DEFAULT_RAMP_PCTS, Inputs } from '../utils/roiCalculations';
type RampKey = 'rampMonth1Pct' | 'rampMonth2Pct' | 'rampMonth3Pct';
interface SummerPestSalesInputsProps {
  inputs: Inputs;
  onChange: (next: Inputs) => void;
  variant?: 'calculator' | 'sidebar';
}
const RAMP_KEYS: RampKey[] = ['rampMonth1Pct', 'rampMonth2Pct', 'rampMonth3Pct'];

/**
 * Summer Pest asks for a new rep's sales in each of their first three months
 * after launch as plain counts. They're stored as ramp percentages of the
 * veteran rate, so the model math stays the same.
 */
export function SummerPestSalesInputs({
  inputs,
  onChange,
  variant = 'calculator'
}: SummerPestSalesInputsProps) {
  const [drafts, setDrafts] = useState<Partial<Record<RampKey, string>>>({});
  const vet = Number(inputs.salesVetMonthly) || 0;
  const salesFor = (key: RampKey, i: number) => {
    const pct = Number(inputs[key] ?? DEFAULT_RAMP_PCTS[i]);
    return Math.round(pct / 100 * vet * 10) / 10;
  };
  const commit = (key: RampKey) => {
    const raw = drafts[key];
    setDrafts(d => ({
      ...d,
      [key]: undefined
    }));
    if (raw === undefined) return;
    const sales = parseFloat(raw);
    if (Number.isNaN(sales) || vet <= 0) return;
    onChange({
      ...inputs,
      [key]: Math.max(0, sales) / vet * 100
    });
  };
  const calculator = variant === 'calculator';
  const inputClass = calculator ? 'w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm' : 'w-full p-2 rounded border border-gray-300 text-sm focus:border-amber outline-none';
  const fields = <div className={calculator ? 'w-56 grid grid-cols-3 gap-1.5' : 'grid grid-cols-3 gap-2'} data-id="element-1724" data-mp="3dilsz342dqic">
      {RAMP_KEYS.map((key, i) => <div key={key} data-id="element-1725" data-mp="3hgrv1xtyjhwx" data-mpi={i}>
          <label htmlFor={`${variant}-summer-${key}`} className={`block text-xs font-semibold mb-1 ${calculator ? 'text-gray-600 text-center' : 'text-gray-700'}`} data-id="element-1726" data-mp="1j3j7dgi4yskp">
            Mo {i + 1}
          </label>
          <input id={`${variant}-summer-${key}`} type="number" min="0" step="1" value={drafts[key] ?? salesFor(key, i)} onChange={e => setDrafts(d => ({
        ...d,
        [key]: e.target.value
      }))} onBlur={() => commit(key)} onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }} className={inputClass} data-id="element-1727" data-mp="2qgjxme9tuh6h" />
        </div>)}
    </div>;
  if (!calculator) {
    return <div className="rounded border border-gray-200 bg-gray-50 p-3" data-id="element-1728" data-mp="34f9xg1o16uyj">
        <div className="text-xs font-bold text-navy uppercase tracking-wide mb-1" data-id="element-1729" data-mp="3j34ntbekgn8g">
          Sales After Launch
        </div>
        <p className="text-xs text-gray-600 mb-3" data-id="element-1730" data-mp="3e64r3r3lwwu0">
          A new rep's sales in each of their first three months after launch.
        </p>
        {fields}
      </div>;
  }
  return <div className="flex items-start gap-3 w-full max-w-xl" data-id="element-1731" data-mp="3n7m7zbou92jr">
      <div className="w-44 text-right pt-6" data-id="element-1732" data-mp="am5ng8mdcwxd">
        <span className="text-sm font-semibold text-gray-700" data-id="element-1733" data-mp="an8ag5gkmf80">
          Sales After Launch
        </span>
        <span className="block text-[11px] leading-snug text-gray-500" data-id="element-1734" data-mp="fjfji749k6u6">
          new rep's months 1–3
        </span>
      </div>
      {fields}
    </div>;
}

/**
 * Under Summer Pest the month 1–3 sales counts stay fixed when the veteran
 * rate changes, so the stored ramp percentages are rescaled to match.
 */
export function withVeteranRate(inputs: Inputs, nextVet: number): Inputs {
  const prevVet = Number(inputs.salesVetMonthly) || 0;
  if (prevVet <= 0 || nextVet <= 0) return {
    ...inputs,
    salesVetMonthly: nextVet
  };
  const next: Inputs = {
    ...inputs,
    salesVetMonthly: nextVet
  };
  RAMP_KEYS.forEach((key, i) => {
    const pct = Number(inputs[key] ?? DEFAULT_RAMP_PCTS[i]);
    next[key] = pct * prevVet / nextVet;
  });
  return next;
}